// URL Generator — pick a platform, get a pre-tagged UTM URL to paste on job boards.
import { useState } from "react";
import { toast } from "sonner";
import { X, Copy, Check, Link2, Globe } from "lucide-react";

const PLATFORMS = [
  { key: "readysethire", label: "ReadySetHire" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "indeed", label: "Indeed" },
  { key: "ziprecruiter", label: "ZipRecruiter" },
  { key: "glassdoor", label: "Glassdoor" },
  { key: "facebook", label: "Facebook / Meta" },
  { key: "google", label: "Google Jobs" },
  { key: "reddit", label: "Reddit" },
  { key: "craigslist", label: "Craigslist" },
  { key: "newsletter", label: "Email Newsletter" },
  { key: "twitter", label: "X / Twitter" },
  { key: "instagram", label: "Instagram" },
  { key: "whatsapp", label: "WhatsApp / Direct" },
  { key: "custom", label: "Custom…" },
];

/**
 * Props:
 *   mode: "job" | "master"
 *   jobId: string (required if mode === "job")
 *   jobTitle: string (optional label)
 *   onClose: () => void
 */
export default function UrlGeneratorModal({ mode = "job", jobId, jobTitle, onClose }) {
  const [platform, setPlatform] = useState("readysethire");
  const [customName, setCustomName] = useState("");
  const [copied, setCopied] = useState(false);

  const origin = process.env.REACT_APP_PUBLIC_URL || "https://directhireteam.com";
  const source = platform === "custom" ? (customName.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") || "custom") : platform;

  const path = mode === "master" ? "/api/careers" : `/api/job/${jobId}`;
  const url = `${origin}${path}?utm_source=${source}&utm_medium=job-board`;

  const humanUrl = mode === "master" ? `${origin}/jobs?utm_source=${source}` : `${origin}/apply/${jobId}?utm_source=${source}`;

  const doCopy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this URL:", val);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="url-generator-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
          <div className="flex items-center gap-2">
            {mode === "master" ? <Globe className="h-5 w-5 text-[#D4AF37]" /> : <Link2 className="h-5 w-5 text-[#10B981]" />}
            <div>
              <h2 className="font-serif text-xl text-navy">{mode === "master" ? "Master Careers URL" : "Job Board URL"}</h2>
              {jobTitle && <p className="text-xs text-navy/55">{jobTitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-navy/55 hover:text-navy" data-testid="urlgen-close"><X className="h-5 w-5" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-navy/55 font-semibold">Step 1 — Pick where you're posting</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPlatform(p.key)}
                  className={`text-left rounded-md px-3 py-2 text-sm font-medium ring-1 transition-all ${platform === p.key ? "bg-[#0F2A1F] text-white ring-[#0F2A1F]" : "bg-white text-navy ring-navy/15 hover:ring-navy/40"}`}
                  data-testid={`urlgen-platform-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {platform === "custom" && (
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Type platform name (e.g. hackernews, angelco)"
                className="mt-3 w-full rounded-md border border-navy/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A1F]"
                data-testid="urlgen-custom-input"
              />
            )}
          </div>

          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-navy/55 font-semibold">Step 2 — Copy the URL below</div>
            <div className="mt-3 rounded-md bg-[#0F2A1F] p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-2">
                For job boards (ReadySetHire, LinkedIn, Indeed) — scraper-friendly
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-white/95 break-all font-mono" data-testid="urlgen-board-url">{url}</code>
                <button onClick={() => doCopy(url)} className="rounded-md bg-[#D4AF37] text-[#0F2A1F] px-3 py-2 text-xs font-bold hover:bg-[#e6c34c] inline-flex items-center gap-1 flex-shrink-0" data-testid="urlgen-copy-board">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="mt-3 rounded-md bg-navy/[0.04] p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-navy/55 font-semibold mb-2">
                For direct sharing (email, social DMs) — pretty React page
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-navy/80 break-all font-mono" data-testid="urlgen-apply-url">{humanUrl}</code>
                <button onClick={() => doCopy(humanUrl)} className="rounded-md ring-1 ring-navy/20 hover:ring-navy/40 px-3 py-2 text-xs font-semibold text-navy inline-flex items-center gap-1 flex-shrink-0" data-testid="urlgen-copy-apply">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-md bg-[#FAF7EE] p-4 text-xs text-navy/70 leading-relaxed">
            <strong className="text-navy">📌 Tip:</strong> Use the <strong>gold URL</strong> when the platform will "import from web" (ReadySetHire, LinkedIn, Indeed). Use the <strong>gray URL</strong> when you're sharing directly (email, Facebook post, WhatsApp).
            After a week, check your Applications tab — you'll see <code className="bg-white px-1 py-0.5 rounded">{source}</code> in the Source column for every applicant from this link.
          </div>
        </div>
      </div>
    </div>
  );
}
